import { useMemo } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import { useUrlParams } from '../hooks/useUrlState';
import { api, AtRiskDeliverableRow, AtRiskReport, downloadFile } from '../lib/api';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  ActionButton, Spinner, EmptyState, MetricCard, IssueTypeBadge, SortableTh, UpdatingBadge,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';
import { fmtMonth } from '../lib/fmt';

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  return `${fmtMonth(`${y}-${m}`)} ${d}`;
}

type BucketId = 'overdue' | 'stale' | 'unassigned';

const BUCKETS: Array<{ id: BucketId; label: string; key: keyof AtRiskReport; badge: 'red' | 'amber' | 'blue'; sub: string }> = [
  { id: 'overdue',    label: 'Overdue',    key: 'overdue',    badge: 'red',   sub: 'Past planned ship date, still open' },
  { id: 'stale',      label: 'Stale',      key: 'stale',      badge: 'amber', sub: '30+ days without activity' },
  { id: 'unassigned', label: 'Unassigned', key: 'unassigned', badge: 'blue',  sub: 'Missing DM or SA' },
];

const AT_RISK_DEFAULTS = { tab: 'overdue', search: '' };

export default function AdminAtRiskPage() {
  const { data, isLoading, isFetching, error, refetch } = useQuery(() => api.admin.atRisk());
  const [params, setParams] = useUrlParams(AT_RISK_DEFAULTS);
  const tab = params.tab as BucketId;
  const search = params.search;
  const setTab    = (v: BucketId) => setParams({ tab: v });
  const setSearch = (v: string)   => setParams({ search: v });

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return null;

  const counts = {
    overdue: data.overdue.length,
    stale: data.stale.length,
    unassigned: data.unassigned.length,
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>At-risk deliverables</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {isFetching && <UpdatingBadge />}
          <ActionButton onClick={() => setParams(AT_RISK_DEFAULTS)} variant="default">Clear filters</ActionButton>
          <ActionButton onClick={() => downloadFile('/admin/at-risk?format=csv', 'at-risk-report.csv')} variant="default">Export CSV</ActionButton>
          <ActionButton onClick={refetch} variant="default">Refresh</ActionButton>
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        {BUCKETS.map((b) => (
          <MetricCard
            key={b.id}
            label={b.label}
            value={counts[b.id]}
            accent={counts[b.id] === 0 ? 'green' : 'amber'}
            sub={b.sub}
          />
        ))}
      </div>

      <Card>
        <CardHeader>
          {BUCKETS.map((b) => (
            <TabButton key={b.id} active={tab === b.id} onClick={() => setTab(b.id)}>
              {b.label} <Badge variant={counts[b.id] === 0 ? 'green' : b.badge}>{counts[b.id]}</Badge>
            </TabButton>
          ))}
          <span style={{ marginLeft: 'auto' }}>
            <input
              type="search"
              placeholder="Filter by key, summary, DM, SA…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                fontSize: 11, padding: '4px 8px', width: 260,
                border: '0.5px solid var(--color-border-strong)',
                borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
              }}
            />
          </span>
        </CardHeader>

        <RiskTable bucket={tab} rows={data[tab]} search={search} />
      </Card>
    </>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-blue)' : '2px solid transparent',
        padding: '8px 16px',
        fontSize: 12,
        fontWeight: 500,
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {children}
    </button>
  );
}

function RiskTable({ bucket, rows, search }: { bucket: BucketId; rows: AtRiskDeliverableRow[]; search: string }) {
  const { config } = useConfig();

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      r.jira_issue_key.toLowerCase().includes(needle)
      || (r.summary ?? '').toLowerCase().includes(needle)
      || (r.tempo_account_name ?? '').toLowerCase().includes(needle)
      || (r.delivery_manager_display_name ?? '').toLowerCase().includes(needle)
      || (r.solution_architect_display_name ?? '').toLowerCase().includes(needle),
    );
  }, [rows, search]);

  const { sorted, sort } = useTableSort(
    filtered,
    {
      key:       (r) => r.jira_issue_key,
      summary:   (r) => r.summary,
      account:   (r) => r.tempo_account_name ?? '',
      dm:        (r) => r.delivery_manager_display_name ?? '',
      sa:        (r) => r.solution_architect_display_name ?? '',
      ship:      (r) => r.planned_ship_date ?? '',
      last:      (r) => r.last_work_date ?? '',
      days:      (r) => r.days_overdue ?? r.days_since_activity ?? 0,
    },
    { key: bucket === 'overdue' ? 'days' : bucket === 'stale' ? 'last' : 'key',
      dir: bucket === 'overdue' ? 'desc' : 'asc' },
  );

  if (sorted.length === 0) {
    return <EmptyState message={search ? 'No matches.' : 'All clear — nothing in this bucket.'} />;
  }

  return (
    <div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
      <Table>
        <thead>
          <tr>
            <SortableTh columnKey="key"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Key</SortableTh>
            <SortableTh columnKey="summary" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Summary</SortableTh>
            <SortableTh columnKey="account" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Engagement</SortableTh>
            <SortableTh columnKey="dm"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>DM</SortableTh>
            <SortableTh columnKey="sa"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>SA</SortableTh>
            {bucket === 'overdue' && <>
              <SortableTh columnKey="ship" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Planned ship</SortableTh>
              <SortableTh columnKey="days" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Days overdue</SortableTh>
            </>}
            {bucket === 'stale' && <>
              <SortableTh columnKey="last" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Last worklog</SortableTh>
              <SortableTh columnKey="days" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Days idle</SortableTh>
            </>}
            {bucket === 'unassigned' && <SortableTh columnKey="ship" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Planned ship</SortableTh>}
            <Th>Jira</Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const url = jiraIssueUrl(config, r.jira_issue_key);
            return (
              <Tr key={r.jira_issue_id}>
                <Td bold><IssueTypeBadge issueType={r.issue_type} />{r.jira_issue_key}</Td>
                <Td>{r.summary}</Td>
                <Td muted style={{ fontSize: 11 }}>{r.tempo_account_name ?? '—'}</Td>
                <Td muted style={{ fontSize: 11 }}>{r.delivery_manager_display_name ?? <em>missing</em>}</Td>
                <Td muted style={{ fontSize: 11 }}>{r.solution_architect_display_name ?? <em>missing</em>}</Td>
                {bucket === 'overdue' && <>
                  <Td>{fmtDate(r.planned_ship_date)}</Td>
                  <Td right><span style={{ color: 'var(--color-red-text)' }}>{r.days_overdue}</span></Td>
                </>}
                {bucket === 'stale' && <>
                  <Td>{r.last_work_date ? fmtDate(r.last_work_date) : <em>never</em>}</Td>
                  <Td right>{r.days_since_activity}</Td>
                </>}
                {bucket === 'unassigned' && <Td>{fmtDate(r.planned_ship_date)}</Td>}
                <Td>
                  {url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--color-blue)', textDecoration: 'underline' }}>
                      Open ↗
                    </a>
                  ) : '—'}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}
